import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { WorkOrderPriceRevisionTypeEnum } from 'gql/graphql';
import { formatDateToLocale } from 'utils';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useMemo } from 'react';

import {
    bimWorkOrderVersionPriceRevisionCodeFilterGql,
    bimWorkOrderVersionPriceRevisionNameFilterGql,
} from '../../WorkOrderVersion/workOrderVersionQueries';
import { BimWorkOrderRowDetailsType } from '../types';

import {
    bimWorkOrderPriceRevisionCodeFilterGql,
    bimWorkOrderPriceRevisionNameFilterGql,
} from './bimWorkOrderPriceRevisionQueries';

const updateTypeDateColumn = {
    headerName: 'Дата изменения',
    field: 'priceUpdateDate',
    width: 240,
    spanHeaderHeight: true,
    valueFormatter: ({ value }: { value: string }) => formatDateToLocale(value),
};

const excludeChildValuesFrom = ['План по НЗ', 'Выполнено по НЗ', 'Остаток по НЗ'];

export const useBimWorkOrderPriceRevisionsColumnsByType = ({
    detailsType,
    columns,
    workOrderPriceId,
    changeFilterHandler,
    isVersionPage,
}: {
    detailsType: BimWorkOrderRowDetailsType;
    workOrderPriceId: number;
    columns: (ColDef | ColGroupDef)[];
    changeFilterHandler: (params: { fieldName: string; checkedValues: string[] }) => void;
    isVersionPage?: boolean;
}) =>
    useMemo(() => {
        const currentVariables = isVersionPage
            ? {
                  workOrderVersionPriceId: workOrderPriceId,
                  revisionType: WorkOrderPriceRevisionTypeEnum.Revision,
              }
            : {
                  workOrderPriceId,
                  revisionType: WorkOrderPriceRevisionTypeEnum.Revision,
              };

        const columnsWithExcludedChild = columns.map((column) => {
            if (column.headerName === 'Шифр') {
                return detailsType === BimWorkOrderRowDetailsType.Revision && 'filterParams' in column
                    ? {
                          ...column,
                          cellRenderer: undefined,
                          filter: AgGridCustomFilterAdapter,
                          filterParams: {
                              ...column.filterParams,
                              variables: currentVariables,
                              changeFilterHandler,
                              dataAccessor: isVersionPage
                                  ? 'bimWorkOrderVersionPriceRevisionCodeFilter'
                                  : 'bimWorkOrderPriceRevisionCodeFilter',
                              query: isVersionPage
                                  ? bimWorkOrderVersionPriceRevisionCodeFilterGql
                                  : bimWorkOrderPriceRevisionCodeFilterGql,
                          },
                      }
                    : {
                          ...column,
                          cellRenderer: undefined,
                      };
            }

            if (
                column.headerName === 'Наименование работ, затрат' &&
                detailsType === BimWorkOrderRowDetailsType.Revision &&
                'filterParams' in column
            ) {
                return {
                    ...column,
                    cellRenderer: undefined,
                    filter: AgGridCustomFilterAdapter,
                    filterParams: {
                        ...column.filterParams,
                        variables: currentVariables,
                        changeFilterHandler,
                        dataAccessor: isVersionPage
                            ? 'bimWorkOrderVersionPriceRevisionNameFilter'
                            : 'bimWorkOrderPriceRevisionNameFilter',
                        query: isVersionPage
                            ? bimWorkOrderVersionPriceRevisionNameFilterGql
                            : bimWorkOrderPriceRevisionNameFilterGql,
                    },
                };
            }

            if (column.headerName && excludeChildValuesFrom.includes(column.headerName) && 'children' in column) {
                return {
                    ...column,
                    children: column.children.filter((_, childIdx) => childIdx < column.children.length - 2),
                };
            }

            return column;
        });

        if (detailsType === BimWorkOrderRowDetailsType.Revision) {
            return columnsWithExcludedChild.filter((_, revisionIdx) => revisionIdx === 0 || revisionIdx >= 2);
        }

        return [
            updateTypeDateColumn,
            ...columnsWithExcludedChild.filter((_, updateIdx) => updateIdx === 0 || updateIdx > 3),
        ];
    }, [changeFilterHandler, columns, detailsType, isVersionPage, workOrderPriceId]);
